import PropTypes from 'prop-types';
import { SimpleShowLayout, usePermissions, useDataProvider, useTranslate, useNotify, Loading } from 'react-admin';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useDrawer } from '@/composables/useDrawer';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import { ContractorLogoField } from '@/components/contractors/ContractorLogoField.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import DeviceDetailsStats from '@/components/device/DeviceDetailsStats.jsx';
import { useEditingStates } from '@/components/service/hooks/useEditingStates';
import { useQuickEditField } from '@/composables/useQuickEditField';
import CollectionPointDetailsFields from './CollectionPointDetailsFields.jsx';
import ServiceNoteSection from './ServiceNoteSection';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection';
import { CollectionPointHistory } from './CollectionPointHistory.jsx';

export const CollectionPointDetailsShow = ({ handleClose, elementId }) => {
    const { permissions } = usePermissions();
    const isAdminOrBok = permissions?.isAdmin || permissions?.isBok;
    const canEditContractor = permissions.isAdmin || permissions.isBok || permissions.isClientMaster;
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    const [collectionPoint, setCollectionPoint] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const loadingState = getLoadingState({ isLoading, error, data: collectionPoint, translate });
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isSummaryOpen, setIsSummaryOpen] = useState(true);
    const [isStatsOpen, setIsStatsOpen] = useState(false);
    const canEdit = permissions.isAdmin || permissions.isBok || permissions.isClientMaster || permissions.isPosOwner || permissions.isServiceMaster;
    const [collectionPointAnalytics, setCollectionPointAnalytics] = useState(null);
    const [isCollectionPointAnalyticsLoading, setIsCollectionPointAnalyticsLoading] = useState(false);
    const [collectionPointAnalyticsError, setCollectionPointAnalyticsError] = useState(null);
    const [periodFilters, setPeriodFilters] = useState({ period: 30 });

    const editingStates = useEditingStates();
    const { save, isLoadingEdit, error: quickEditError } = useQuickEditField('collection-point', elementId, editingStates);

    const canEditServiceNote = permissions?.isAdmin || permissions?.isServiceMaster;
    const canViewServiceNote = permissions?.isAdmin || permissions?.isServiceMaster || permissions?.isService || permissions?.isBok;

    useEffect(() => {
        if (quickEditError?.status === 400) {
            notify(translate('common.errors.updateFieldFailed'), { type: 'error', autoHideDuration: null, multiLine: true });
        }
    }, [quickEditError, notify, translate]);

    const handleSave = useCallback(
        async formData => {
            await save(formData);
            const { data: updatedCollectionPoint } = await dataProvider.getOne('collection-point', { id: elementId });
            setCollectionPoint(updatedCollectionPoint);
        },
        [save, dataProvider, elementId],
    );

    const fetchCollectionPointAnalytics = useCallback(() => {
        setIsCollectionPointAnalyticsLoading(true);
        dataProvider
            .getList('analytic-transactions', {
                filter: { collectionPointId: elementId, ...periodFilters },
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'DESC' },
            })
            .then(({ data }) => {
                setCollectionPointAnalytics(data);
                setCollectionPointAnalyticsError(null);
            })
            .catch(err => {
                setCollectionPointAnalytics(null);
                setCollectionPointAnalyticsError(err);
            })
            .finally(() => setIsCollectionPointAnalyticsLoading(false));
    }, [dataProvider, elementId, periodFilters]);

    useEffect(() => {
        if (elementId) {
            setIsLoading(true);
            dataProvider
                .getOne('collection-point', { id: elementId })
                .then(({ data }) => {
                    setCollectionPoint(data);
                    setError(null);
                })
                .catch(err => {
                    console.error('Error fetching collectionPoint:', err);
                    setError(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [dataProvider, elementId]);

    useEffect(() => {
        if (elementId) {
            fetchCollectionPointAnalytics();
        }
    }, [fetchCollectionPointAnalytics, elementId]);

    if (loadingState || isLoadingEdit) return loadingState || <Loading />;

    const detailsFieldSx = {
        '& .MuiStack-root.ra-field': {
            padding: '20px 0 !important',
            [theme.breakpoints.down('md')]: {
                padding: '20px 0 16px 0 !important',
            },
            '& .RaLabeled-label': {
                width: '220px !important',
                minWidth: '220px !important',
                lineHeight: '20px !important',
                color: `${Palette.black} !important`,
                [theme.breakpoints.down('md')]: {
                    width: '100% !important',
                    minWidth: '100% !important',
                    marginBottom: '-2px',
                },
            },

            '& .MuiTypography-root.MuiTypography-body2, & .MuiTypography-root.MuiTypography-caption': {
                fontWeight: '600 !important',
                '&.no-bold': {
                    fontWeight: '400 !important',
                },
            },
        },
    };

    const summaryContent = (
        <SimpleShowLayout
            record={collectionPoint}
            sx={{
                '& .MuiStack-root > *:not(style) ~ *:not(style)': {
                    marginTop: '0 !important',
                },
            }}
        >
            <Box className='details-sample-list' sx={detailsFieldSx}>
                {collectionPoint.contractor && (
                    <Box
                        sx={{
                            marginBottom: '40px',
                            cursor: canEditContractor ? 'pointer' : 'default',
                            maxWidth: 'max-content',
                            [theme.breakpoints.down('md')]: {
                                marginBottom: '0 !important',
                                padding: '20px 0 !important',
                            },
                        }}
                        onClick={canEditContractor ? () => openDrawer(collectionPoint.contractorId, 'contractor-edit') : undefined}
                    >
                        <ContractorLogoField contractorSource='contractor' source='logo' />
                    </Box>
                )}
                <CollectionPointDetailsFields collectionPoint={collectionPoint} isAdminOrBok={isAdminOrBok} />
            </Box>
            <Box
                className='details-sample-list'
                sx={{
                    marginTop: '0 !important',
                    '&.details-sample-list': {
                        marginTop: '0 !important',
                    },
                    '& .MuiStack-root.ra-field': {
                        borderTop: 'none !important',
                        '& .RaLabeled-label': {
                            width: '220px !important',
                            minWidth: '220px !important',
                            lineHeight: '20px !important',
                            color: `${Palette.black} !important`,
                            [theme.breakpoints.down('md')]: {
                                width: '130px !important',
                                minWidth: '130px !important',
                            },
                        },
                    },
                    '& .editable-field-content': {
                        '& > div': {
                            maxHeight: 'fit-content',
                        },
                    },
                }}
            >
                <ServiceNoteSection
                    elementId={elementId}
                    collectionPoint={collectionPoint}
                    handleSave={handleSave}
                    canEditServiceNote={canEditServiceNote}
                    canViewServiceNote={canViewServiceNote}
                    editingStates={editingStates}
                />
            </Box>
        </SimpleShowLayout>
    );

    const statsContent = (
        <DeviceDetailsStats
            analyticData={collectionPointAnalytics}
            isTransactionStatsLoading={isCollectionPointAnalyticsLoading}
            transactionStatsError={collectionPointAnalyticsError}
            periodFilters={periodFilters}
            setPeriodFilters={setPeriodFilters}
            elementId={elementId}
            isCollectionPoint={true}
            hideTitle={isMobile}
        />
    );

    const canShowStats = collectionPointAnalytics && collectionPointAnalytics.length >= 2 && collectionPointAnalytics[0] && collectionPointAnalytics[1];

    return (
        <Box>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('resources.collectionPoint.labels.detailsTitle')}</Typography>
                    <Box className='popup-box__more-actions-grey' sx={{ height: '44px' }}>
                        {canEdit && (
                            <CustomButton
                                color='secondary'
                                label={translate('resources.collectionPoint.labels.edit')}
                                variant='contained'
                                size={isDesktop ? 'large' : 'small'}
                                startIcon={<EditIcon />}
                                onClick={() => openDrawer(elementId, 'collection-point-edit')}
                            />
                        )}
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            {isMobile ? (
                <Box className='pos-details-collapse-wrapper'>
                    <CollapsibleSection
                        title={translate('resources.collectionPoint.labels.summary')}
                        isOpen={isSummaryOpen}
                        onToggle={() => setIsSummaryOpen(!isSummaryOpen)}
                    >
                        {summaryContent}
                    </CollapsibleSection>
                    {canShowStats && (
                        <CollapsibleSection
                            title={translate('resources.collectionPoint.labels.statisticsForCollectionPoint')}
                            isOpen={isStatsOpen}
                            onToggle={() => setIsStatsOpen(!isStatsOpen)}
                        >
                            {statsContent}
                        </CollapsibleSection>
                    )}
                    <CollectionPointHistory collectionPointId={elementId} />
                </Box>
            ) : (
                <>
                    <Box className='popup-wrapper border-bottom no-padding-bottom'>
                        <Box className='popup-box'>
                            {summaryContent}
                            {statsContent}
                        </Box>
                    </Box>
                    <CollectionPointHistory collectionPointId={elementId} />
                </>
            )}
        </Box>
    );
};

CollectionPointDetailsShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
